import { useRef, useState } from 'react';
import { apiClient } from '../../../auth-lib';
import { Button, Modal, useToast } from '../../../design-system';
import '../../styles/ImportClients.css';

// Download-template -> fill in Excel -> upload -> partial-success report.
// Mirrors the shape of the tasks bulk endpoints: every row is attempted
// independently, so one bad row never blocks the rest of the file.
function ImportClientsModal({ isOpen, onClose, onImported }) {
  const toast = useToast();
  const fileRef = useRef(null);
  const [downloading, setDownloading] = useState(false);
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const reset = () => {
    setFile(null);
    setResult(null);
    setError('');
    if (fileRef.current) fileRef.current.value = '';
  };

  const close = () => {
    reset();
    onClose();
  };

  const downloadTemplate = async () => {
    setDownloading(true);
    try {
      const res = await apiClient.get('/api/clients/import/template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'client-import-template.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.danger('Failed to download the template');
    } finally {
      setDownloading(false);
    }
  };

  const runImport = async () => {
    if (!file) return;
    setImporting(true);
    setError('');
    setResult(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await apiClient.post('/api/clients/import', form);
      setResult(res.data);
      if (res.data.createdCount > 0) {
        toast.success(`Created ${res.data.createdCount} of ${res.data.totalRows} client${res.data.totalRows === 1 ? '' : 's'}`);
        onImported?.();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to import this file');
    } finally {
      setImporting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      title="Import clients from a spreadsheet"
      size="lg"
      footer={<Button variant="secondary" onClick={close}>{result ? 'Done' : 'Cancel'}</Button>}
    >
      <div className="import-clients">
        {!result && (
          <>
            <div className="import-step">
              <div className="import-step-n">1</div>
              <div className="import-step-body">
                <p><strong>Download the template</strong></p>
                <p className="hint-text">
                  An Excel file with the columns we accept, dropdowns for anything with a fixed
                  set of values, and a sheet listing your staff's emails (for Assigned
                  Partner / Manager). One row = one client.
                </p>
                <Button variant="secondary" size="sm" onClick={downloadTemplate} loading={downloading}>
                  ⇩ Download template (.xlsx)
                </Button>
              </div>
            </div>

            <div className="import-step">
              <div className="import-step-n">2</div>
              <div className="import-step-body">
                <p><strong>Fill it in and upload it back</strong></p>
                <p className="hint-text">Fill in one row per client — as many or as few as you have.</p>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".xlsx"
                  className="ds-input"
                  onChange={(e) => { setFile(e.target.files?.[0] || null); setError(''); }}
                />
              </div>
            </div>

            {error && <div className="form-error" role="alert">{error}</div>}

            <div className="import-actions">
              <Button variant="primary" onClick={runImport} loading={importing} disabled={!file}>
                {importing ? 'Importing…' : 'Import'}
              </Button>
            </div>
          </>
        )}

        {result && (
          <div className="import-result">
            <div className="import-summary">
              <span className="import-summary-ok">{result.createdCount} created</span>
              {result.errorCount > 0 && <span className="import-summary-bad">{result.errorCount} failed</span>}
              <span className="hint-text">of {result.totalRows} row{result.totalRows === 1 ? '' : 's'}</span>
            </div>

            {result.errors.length > 0 && (
              <div className="import-errors">
                <p className="ds-field-label">What went wrong</p>
                <div className="import-errors-list">
                  {result.errors.map((e) => (
                    <div key={e.row} className="import-error-row">
                      <span className="import-error-row-n">Row {e.row}</span>
                      <span className="import-error-row-name">{e.displayName}</span>
                      <span className="import-error-row-msg">{e.error}</span>
                    </div>
                  ))}
                </div>
                <p className="hint-text">
                  Fix these rows in your spreadsheet and upload it again — rows that already
                  succeeded won't be created twice as long as you remove them first.
                </p>
              </div>
            )}

            {result.createdCount > 0 && (
              <Button variant="secondary" size="sm" onClick={reset}>Import another file</Button>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}

export default ImportClientsModal;
